import { ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { Row, Col, Radio, Button, Select } from 'antd';
import { Formik, FormikHelpers } from 'formik';
import { Form, Input, SubmitButton } from 'formik-antd';
import { useScrollTop } from 'hooks';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
// @ts-ignore
import zipcodeJa from 'zipcode-ja';
import { Modal, ModalWarningLeavePage } from 'components';
import { RegisterRequest, t_cpap_instructions } from 'types';
import { resetItemUpdate } from '../../CpapDetail/slices';
import { newPatient } from '../slices';
import { year, month, date, convertSpaceToFullWidth } from './data';
import schema from './schema';
import Wrap from './styles';
import { getDataStoreFields, getMedicalInstitution } from './thunk';

type Props = {
  nextStep: () => void;
};

type Bod = {
  year: string;
  month: string;
  date: string;
};

const RegisterFirstStep: FC<Props> = ({ nextStep }) => {
  useScrollTop();
  const dispatch = useDispatch();
  const yearOption = year();
  const monthOption = month();
  const { cpapInstructions, dataStoreFields } = useSelector((state: RootState) => state.register);
  const { currentApplicationId } = useSelector((state: RootState) => state.workSpaces);
  const [radio, setRadio] = useState<string>(cpapInstructions.gender || '');
  const [zipcodeLeft, setZipcodeLeft] = useState<string>(cpapInstructions.zip_code?.slice(0, 3) || '');
  const [zipcodeRight, setZipcodeRight] = useState<string>(cpapInstructions.zip_code?.slice(4, 8) || '');
  const [bod, setBod] = useState<Bod>({
    year: cpapInstructions.patient_birthday?.slice(0, 4) || '',
    month: cpapInstructions.patient_birthday?.slice(4, 6) || '',
    date: cpapInstructions.patient_birthday?.slice(6) || '',
  });
  const dateSelect = date(+bod.year, +bod.month);

  useEffect(() => {
    dispatch(getMedicalInstitution());
    dispatch(resetItemUpdate);
  }, [dispatch]);

  useEffect(() => {
    currentApplicationId &&
      dispatch(getDataStoreFields({ appId: currentApplicationId, dataStoreId: 't_cpap_instructions' }));
  }, [dispatch, currentApplicationId]);

  const genders = useMemo(() => {
    const result = dataStoreFields?.find(([_key, value]: [string, any]) => value.display_id === 'gender');
    if (!result) {
      return [];
    }
    return result[1].options;
  }, [dataStoreFields]);

  let initialValue = cpapInstructions.patient_name
    ? cpapInstructions
    : {
        patient_name_kana: '',
        patient_name: '',
        address_1: '',
        phone_number: '',
        facility_name: '',
        patient_id: '',
        address_2: '',
        mobile_phone_number: '',
        in_charge_of_sales: '',
      };

  const handleSubmit = (values: t_cpap_instructions, form: FormikHelpers<RegisterRequest>) => {
    form.setSubmitting(false);

    const zip_code: string | undefined =
      zipcodeLeft === undefined || zipcodeRight === undefined ? undefined : `${zipcodeLeft}-${zipcodeRight}`;
    let patient_birthday: string | undefined = `${bod.year}${bod.month}${bod.date}`;
    if (bod.month === '' || bod.date === '' || bod.year === '') {
      patient_birthday = undefined;
    }
    const patientInformation = {
      zip_code: zip_code,
      patient_id: values.patient_id,
      patient_name: values.patient_name,
      patient_name_kana: values.patient_name_kana,
      patient_birthday: patient_birthday,
      gender: radio,
      address_1: values.address_1,
      address_2: values.address_2,
      mobile_phone_number: values.mobile_phone_number,
      phone_number: values.phone_number,
      facility_name: values.facility_name,
      in_charge_of_sales: values.in_charge_of_sales,
    };
    dispatch(newPatient({ ...cpapInstructions, ...patientInformation }));

    nextStep();
  };

  const onChangeZipcodeLeft = (e: React.ChangeEvent<HTMLInputElement>) => {
    setZipcodeLeft(e.target.value);
  };
  const onChangeZipcodeRight = (e: React.ChangeEvent<HTMLInputElement>) => {
    setZipcodeRight(e.target.value);
  };

  const handleZipCode = () => {
    const zipCode: string = `${zipcodeLeft}${zipcodeRight}`;
    const address = zipcodeJa[zipCode]?.address;
    const result = !address ? '' : address.join(' ');
    return result;
  };

  const onChangeYear = (value: string) => {
    setBod({ ...bod, year: value });
  };
  const onChangeMonth = (value: string) => {
    setBod({ ...bod, month: value });
  };
  const onChangeDate = (dateString: string) => {
    setBod({ ...bod, date: dateString });
  };

  const changeGender = (e: any) => {
    setRadio(e.target.value);
  };

  return (
    <Wrap>
      <p className="standardFont">
        <ExclamationCircleOutlined className="exclamation" />
        患者の医療機関が新規医療機関の場合、先に医療機関の登録を行ってください。
      </p>
      <span className="standardFont ※">※</span>
      <span className="standardFont">は必須項目</span>
      <Formik onSubmit={handleSubmit} initialValues={initialValue} validationSchema={schema}>
        {({ setFieldValue, isValid }) => (
          <Form autoComplete={`${Math.random()}`}>
            <div className="form">
              <Row>
                <Col span={5}>
                  <div className="titleInput">
                    <span className="standardBoldFont space">氏名</span>
                    <span className="standardFont ※">※</span>
                  </div>
                </Col>
                <Col span={6}>
                  <div>
                    <Form.Item name="patient_name">
                      <Input
                        className="input"
                        name="patient_name"
                        placeholder="例：山田　太郎"
                        autoComplete={`${Math.random()}`}
                        onChange={(e) => {
                          const fullWidthValue = convertSpaceToFullWidth(e.target.value);
                          setFieldValue('patient_name', fullWidthValue);
                        }}
                      />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col span={5}>
                  <div className="titleInput">
                    <span className="standardBoldFont space">ふりがな</span>
                    <span className="standardFont ※">※</span>
                  </div>
                </Col>
                <Col span={6}>
                  <div>
                    <Form.Item name="patient_name_kana">
                      <Input
                        name="patient_name_kana"
                        placeholder="例：やまだ　たろう"
                        autoComplete={`${Math.random()}`}
                        onChange={(e) => {
                          const fullWidthValue = convertSpaceToFullWidth(e.target.value);
                          setFieldValue('patient_name_kana', fullWidthValue);
                        }}
                      />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col span={5}>
                  <div className="titleInput">
                    <span className="standardBoldFont space">患者ID</span>
                  </div>
                </Col>
                <Col span={12}>
                  <div>
                    <Form.Item name="patient_id">
                      <Input name="patient_id" autoComplete={`${Math.random()}`} />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col span={5}>
                  <div className="titleInput">
                    <span className="standardBoldFont space">生年月日</span>
                  </div>
                </Col>
                <Col span={19}>
                  <div style={{ display: 'flex' }}>
                    <Form.Item name="year">
                      <Select
                        value={bod.year !== '' ? bod.year : undefined}
                        onChange={onChangeYear}
                        options={yearOption}
                        style={{ width: 133, marginBottom: '22px' }}
                        placeholder=""
                      />
                    </Form.Item>
                    <p className="standardFont date">年</p>
                    <Form.Item name="month">
                      <Select
                        value={bod.month !== '' ? bod.month : undefined}
                        options={monthOption}
                        onChange={onChangeMonth}
                        style={{ width: 100, marginBottom: '22px' }}
                        placeholder=""
                      />
                    </Form.Item>
                    <p className="standardFont date">月</p>
                    <Form.Item name="date">
                      <Select
                        value={bod.date}
                        options={dateSelect}
                        onChange={onChangeDate}
                        style={{ width: 100, marginBottom: '22px' }}
                        placeholder=""
                      />
                    </Form.Item>
                    <p className="standardFont date">日</p>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col span={5}>
                  <div className="titleInput">
                    <span className="standardBoldFont space">性別</span>
                  </div>
                </Col>
                <Col span={19}>
                  <div>
                    <Form.Item name="Gender">
                      <Radio.Group value={radio}>
                        {genders.map((g: any, i: number) => (
                          <Radio key={i} onClick={(e) => changeGender(e)} value={g.option_id}>
                            {g.value}
                          </Radio>
                        ))}
                      </Radio.Group>
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col offset={5} span={19}>
                  <p className="standardFont" style={{ marginBottom: 0 }}>
                    郵便番号
                  </p>
                  <div className="d-flex">
                    <Form.Item name="codeLeft">
                      <Input
                        style={{ width: '92px' }}
                        onChange={(e) => onChangeZipcodeLeft(e)}
                        name="codeLeft"
                        value={zipcodeLeft}
                        placeholder="例：576"
                        maxLength={3}
                        autoComplete={`${Math.random()}`}
                      />
                    </Form.Item>
                    <span className="date">-</span>
                    <Form.Item name="codeRight">
                      <Input
                        style={{ width: '127px', marginRight: '5px' }}
                        name="codeRight"
                        placeholder="例：0033"
                        value={zipcodeRight}
                        maxLength={4}
                        onChange={(e) => onChangeZipcodeRight(e)}
                        autoComplete={`${Math.random()}`}
                      />
                    </Form.Item>
                    <Button
                      onClick={() => {
                        setFieldValue('address_1', handleZipCode());
                      }}
                      className="btnDefault"
                    >
                      住所検索
                    </Button>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col span={5}>
                  <div className="titleInput">
                    <span className="standardBoldFont space">住所</span>
                    <span className="standardFont ※">※</span>
                  </div>
                </Col>
                <Col span={13}>
                  <div>
                    <p className="standardFont" style={{ marginBottom: 0 }}>
                      都道府県/市区町村/番地
                    </p>
                    <Form.Item name="address_1">
                      <Input name="address_1" placeholder="例：大阪府 交野市 5-6-4" autoComplete={`${Math.random()}`} />
                    </Form.Item>
                    <p className="standardFont mb-0">マンション・ビル・建物</p>
                    <Form.Item name="address_2">
                      <Input name="address_2" placeholder="例：スリープビル101号" autoComplete={`${Math.random()}`} />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col span={5}>
                  <div className="titleInput">
                    <span className="standardBoldFont space">携帯番号</span>
                  </div>
                </Col>
                <Col span={13}>
                  <div>
                    <Form.Item name="mobile_phone_number">
                      <Input
                        name="mobile_phone_number"
                        placeholder="例：080-000-0000"
                        autoComplete={`${Math.random()}`}
                      />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col span={5}>
                  <div className="titleInput">
                    <span className="standardBoldFont space">電話番号</span>
                  </div>
                </Col>
                <Col span={13}>
                  <div>
                    <Form.Item name="phone_number">
                      <Input name="phone_number" autoComplete={`${Math.random()}`} />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <Modal valueSelected={cpapInstructions} onChangeSearch={setFieldValue} />
            </div>
            {!isValid && (
              <div style={{ marginTop: '0.5rem' }} className="error">
                <WarningOutlined />
                <p>入力内容に不備がございます。エラーが表示されている項目をご確認ください。</p>
              </div>
            )}
            <div className="submit">
              <p className="standardBoldFont text">以上の内容でよろしければCPAP指示書入力へ進みます。</p>
              <SubmitButton className="btnDefault btn">次 へ</SubmitButton>
            </div>
          </Form>
        )}
      </Formik>
      <ModalWarningLeavePage />
    </Wrap>
  );
};

export default RegisterFirstStep;
